import { Component, computed, effect, inject, input, model, signal, Signal } from '@angular/core';
import { MatTableController } from 'src/app/mat-table-controller';
import { ConfirmationDialogComponent } from 'src/app/Shared/confirmation-dialog/confirmation-dialog.component';
import { BusinessLicense, getFullAddress, License, LICENSE_STATUS } from 'src/model/business';
import { BusinessStore } from 'src/model/business/business-store';
import { BusinessService } from 'src/services/business.service';
import { SharedService } from '../../Shared/shared-service.service';

@Component({
    selector: 'app-license-list',
    templateUrl: './license-list.component.html',
    styleUrl: './license-list.component.scss',
    providers: [MatTableController],
    standalone: false
})
export class LicenseListComponent {
  private sharedService = inject(SharedService);
  private businessService = inject(BusinessService);
  private store = inject(BusinessStore);
  protected table = inject(MatTableController);

  LICENSE_STATUS = LICENSE_STATUS;
  businessList = this.store.businessList;
  branchList = this.store.branchList;
  licenseList = this.store.licenseList;
  businessLicenseList = this.store.businessLicenseList;
  searchText = input<string>();

  showValid = signal(true);
  showInvalid = signal(false);
  showExpired = signal(false);

  branchIdFilter = model<number>(undefined);
  status: Signal<number[]> = computed(() => {
    const status:  number[] = [];
    if (this.showValid()) status.push(LICENSE_STATUS.VALID, LICENSE_STATUS.EXPIRING);
    if (this.showExpired()) status.push(LICENSE_STATUS.EXPIRED);
    if (this.showInvalid()) status.push(LICENSE_STATUS.INVALID);
    return status;
  });

  filteredBusinessLicenseList: Signal<BusinessLicense[]> = computed(() => {
    const license = this.businessLicenseList();
    return license?.length > 0 ? license.filter((l) => (this.branchIdFilter() === undefined || l.BranchId === this.branchIdFilter()) && (this.status().length === 0 || this.status().includes(l.Status))) : [];
  });

  isBoss = computed(() => this.sharedService.isBossAccount());
	displayedColumns = computed(() => this.isBoss() ? [...this.table.columnDefs(), 'Actions'] : this.table.columnDefs());

  constructor() {
    this.table.config.set([
      { columnDef: 'BusinessName', header: 'Business Name' },
      { columnDef: 'BranchName', header: 'Branch Alias' },
      { columnDef: 'AccountNo', header: 'Account No.' },
      { columnDef: 'ContactNo', header: 'Contact No.' },
      { columnDef: 'Email', header: 'Email' },
      { columnDef: 'Address', header: 'Address', calculated: (row) => getFullAddress(row.Address) },
      { columnDef: 'ValidUntil', header: 'Valid Until', type: 'date', format: 'dd MMM yyyy' },
      { columnDef: 'Remark', header: 'Remark' }
    ])
    effect(() => {
      this.refreshDataSource();
    });
  }

  refreshDataSource() {
    this.table.rawData.set([{items: this.filteredBusinessLicenseList()}]);
    this.table.filterQuery.set(this.searchText() ?? '');
  }

  updateLicense(license: License) {
    this.store.updateLicense(license);
  }

  renewLicense(license: License) {
    this.store.renewLicense(license);
  }

  async revalidateLicense(license: License) {
    const l = await this.businessService.putRevalidateLicense(license.Id);
    l.BusinessId = license.BusinessId;
    l.BranchId = license.BranchId;
    this.licenseList.update(list => {
      const i = list.findIndex(l => l.Id === license.Id);
      list.splice(i, 1, l);
      return [...list];
    });
  }

  invalidateLicense(license: License) {
    const modalRef = this.sharedService.openModal(ConfirmationDialogComponent);
    modalRef.componentInstance.message = `Are you sure you want to invalidate this license?`;
    modalRef.result.then(async confirm => {
      if (confirm) {
        const l = await this.businessService.putInvalidateLicense(license.Id);
        l.BusinessId = license.BusinessId;
        l.BranchId = license.BranchId;
        this.licenseList.update(list => {
          const i = list.findIndex(l => l.Id === license.Id);
          list.splice(i, 1, l);
          return [...list];
        });
      }
    });
  }
}
